/**
 * MobileBlockCard Component
 * 
 * Touch-friendly block preview card for the canvas view.
 * Shows icon, title, and subtitle. Taps to expand.
 * Supports drag handle for reordering when draggable prop is true.
 * Swipe left to reveal delete button.
 */

import React, { useState, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './mobile.css';

export interface MobileBlockCardProps {
    id: string;
    type: 'note' | 'pdf' | 'youtube' | 'quiz' | 'slide-deck' | 'explainer';
    title: string;
    subtitle?: string;
    preview?: string; // For notes: first 2-3 lines of content
    content?: unknown; // Block content to check for loading states
    onClick: (id: string) => void;
    onDelete?: (id: string) => void;
    draggable?: boolean;
}

const MobileBlockCard: React.FC<MobileBlockCardProps> = ({
    id,
    type,
    title,
    subtitle,
    preview,
    content,
    onClick,
    onDelete,
    draggable = false,
}) => {
    const [showDelete, setShowDelete] = useState(false);
    const touchStartX = useRef<number>(0);
    const touchEndX = useRef<number>(0);

    // Check if block is in loading/analyzing/generating state
    const isLoading = React.useMemo(() => {
        if (!content || typeof content !== 'object') return false;
        const c = content as Record<string, unknown>;
        // YouTube: analyzing flag
        if (type === 'youtube' && c.analyzing === true) return true;
        // PDF: analyzing flag
        if (type === 'pdf' && (c.analyzing === true || c.status === 'processing')) return true;
        // Quiz: generating flag
        if (type === 'quiz' && c.generating === true) return true;
        // Explainer/SlideDeck: generating flag
        if ((type === 'explainer' || type === 'slide-deck') && c.generating === true) return true;
        return false;
    }, [content, type]);

    // Get loading label based on type
    const loadingLabel = React.useMemo(() => {
        switch (type) {
            case 'youtube': return 'Analyzing video...';
            case 'pdf': return 'Analyzing PDF...';
            case 'quiz': return 'Generating quiz...';
            case 'explainer':
            case 'slide-deck': return 'Generating slides...';
            default: return 'Loading...';
        }
    }, [type]);

    // Drag-drop with dnd-kit
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled: !draggable });

    const dragStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 50 : 1,
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
        touchEndX.current = e.touches[0].clientX; // Initialize to same value
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        // Only process if actual touch movement occurred (not mouse clicks)
        if (touchStartX.current === 0 || touchStartX.current === touchEndX.current) {
            touchStartX.current = 0;
            touchEndX.current = 0;
            return;
        }

        const diff = touchStartX.current - touchEndX.current;
        // Swipe left to show delete
        if (diff > 60) {
            setShowDelete(true);
        } else if (diff < -60) {
            setShowDelete(false);
        }
        touchStartX.current = 0;
        touchEndX.current = 0;
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete?.(id);
    };

    const getIcon = () => {
        switch (type) {
            case 'note':
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                );
            case 'youtube':
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                        <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
                    </svg>
                );
            case 'quiz':
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                );
            case 'pdf':
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                    </svg>
                );
            case 'explainer':
            case 'slide-deck':
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                );
            default:
                return (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    </svg>
                );
        }
    };

    const getTypeLabel = () => {
        switch (type) {
            case 'note': return 'Note';
            case 'youtube': return 'YouTube';
            case 'quiz': return 'Quiz';
            case 'pdf': return 'PDF';
            case 'explainer': return 'Explainer';
            case 'slide-deck': return 'Slides';
            default: return 'Block';
        }
    };

    // For notes, render inline with left border (like desktop)
    if (type === 'note') {
        return (
            <div
                ref={setNodeRef}
                style={dragStyle}
                className={`mobile-note-inline-wrapper ${isDragging ? 'dragging' : ''}`}
                {...attributes}
            >
                <div
                    className="mobile-note-inline"
                    onClick={() => !isDragging && onClick(id)}
                    role="button"
                    tabIndex={0}
                >
                    {/* Drag Handle for notes */}
                    {draggable && (
                        <div
                            className="mobile-note-inline-drag"
                            aria-label="Drag to reorder"
                            {...listeners}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <circle cx="9" cy="5" r="1.5" />
                                <circle cx="9" cy="12" r="1.5" />
                                <circle cx="9" cy="19" r="1.5" />
                                <circle cx="15" cy="5" r="1.5" />
                                <circle cx="15" cy="12" r="1.5" />
                                <circle cx="15" cy="19" r="1.5" />
                            </svg>
                        </div>
                    )}
                    {/* Left border and content */}
                    <div className="mobile-note-inline-content">
                        <p className={`mobile-note-inline-preview ${!preview && !subtitle ? 'empty' : ''}`}>
                            {preview || subtitle || 'Empty note'}
                        </p>
                        <span className="mobile-note-inline-hint">tap to edit</span>
                    </div>
                </div>
            </div>
        );
    }

    // For other block types, render as cards
    return (
        <div
            ref={setNodeRef}
            style={dragStyle}
            className={`mobile-block-card-wrapper ${showDelete ? 'show-delete' : ''} ${isDragging ? 'dragging' : ''}`}
            {...attributes}
        >
            <div
                className={`mobile-block-card ${draggable ? 'draggable' : ''} ${isLoading ? 'loading' : ''}`}
                onClick={() => !showDelete && !isDragging && !isLoading && onClick(id)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                role="button"
                tabIndex={isLoading ? -1 : 0}
                aria-disabled={isLoading}
            >
                {/* Drag Handle */}
                {draggable && (
                    <div
                        className="mobile-block-card-drag"
                        aria-label="Drag to reorder"
                        {...listeners}
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="9" cy="5" r="1.5" />
                            <circle cx="9" cy="12" r="1.5" />
                            <circle cx="9" cy="19" r="1.5" />
                            <circle cx="15" cy="5" r="1.5" />
                            <circle cx="15" cy="12" r="1.5" />
                            <circle cx="15" cy="19" r="1.5" />
                        </svg>
                    </div>
                )}
                <div className={`mobile-block-card-icon ${type}`}>
                    {getIcon()}
                </div>
                <div className="mobile-block-card-body">
                    <h3 className="mobile-block-card-title">{title}</h3>
                    {isLoading ? (
                        <div className="mobile-block-card-loading">
                            <svg className="mobile-block-card-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10" strokeDasharray="60" strokeLinecap="round" />
                            </svg>
                            <span>{loadingLabel}</span>
                        </div>
                    ) : (
                        <p className="mobile-block-card-subtitle">
                            {subtitle || getTypeLabel()}
                        </p>
                    )}
                </div>
            </div>

            {/* Delete button revealed on swipe */}
            {onDelete && (
                <button
                    className="mobile-block-card-delete"
                    onClick={handleDelete}
                    aria-label="Delete block"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                </button>
            )}
        </div>
    );
};

export default MobileBlockCard;
